import { useMutation } from '@apollo/client';
import type {
  InsertDepartmentTypeWithGroupNameMutation,
  InsertDepartmentTypeWithGroupNameMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertDepartmentTypeWithGroupNameDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertDepartmentTypeWithGroupNameTRPC } from './useInsertDepartmentTypeWithGroupNameTRPC';

export const useInsertDepartmentTypeWithGroupName = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertDepartmentTypeWithGroupNameGraphQL, graphqlState] = useMutation(
    InsertDepartmentTypeWithGroupNameDocument,
    {
      update: (cache) => {
        evictField(cache, 'department_type');
        evictField(cache, 'department_type_group');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertDepartmentTypeWithGroupNameTRPC();

  const insertDepartmentTypeWithGroupName = async (
    variables: InsertDepartmentTypeWithGroupNameMutationVariables
  ): Promise<InsertDepartmentTypeWithGroupNameMutation> => {
    if (trpcEnabled) {
      if (!variables.Name) {
        throw new Error('Name is required');
      }

      return trpcMutation.insertDepartmentTypeWithGroupName({
        Name: variables.Name,
        Description: variables.Description,
        DepartmentGroupName: variables.DepartmentGroupName,
      });
    }

    const result = await insertDepartmentTypeWithGroupNameGraphQL({
      variables,
    });
    if (!result.data) {
      throw new Error('Failed to insert department type');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertDepartmentTypeWithGroupName,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
      data: trpcMutation.data,
    };
  }

  return {
    insertDepartmentTypeWithGroupName,
    loading: graphqlState.loading,
    error: graphqlState.error,
    data: graphqlState.data,
  };
};
